import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import groupsService from '../../services/groupsService'
import { Group, Permission, CreateGroupRequest } from '../../services/types'
import { ConfirmModal } from '../../components/ConfirmModal'

interface GroupFormData {
  name: string
  permissions: Set<number>
}

const emptyForm: GroupFormData = {
  name: '',
  permissions: new Set(),
}

export const GroupsPage = () => {
  const toast = useToast()
  const queryClient = useQueryClient()

  // State
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingGroup, setEditingGroup] = useState<Group | null>(null)
  const [formData, setFormData] = useState<GroupFormData>(emptyForm)
  const [formError, setFormError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null)

  // Fetch groups
  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupsService.getAll(),
  })

  // Fetch permissions
  const { data: allPermissions = [] } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => groupsService.getPermissions(),
  })

  // Group permissions by app
  const permissionsByApp = useMemo(() => {
    const grouped: Record<string, Permission[]> = {}
    for (const perm of allPermissions) {
      const app = perm.content_type_display
      if (!grouped[app]) grouped[app] = []
      grouped[app].push(perm)
    }
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
  }, [allPermissions])

  // Filter groups by search
  const filteredGroups = groups.filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  )

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateGroupRequest) => groupsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      closeForm()
    },
    onError: (error: any) => {
      setFormError(
        error.response?.data?.detail ||
          error.response?.data?.name?.[0] ||
          JSON.stringify(error.response?.data) ||
          'Erro ao criar grupo.'
      )
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateGroupRequest> }) =>
      groupsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      closeForm()
    },
    onError: (error: any) => {
      setFormError(
        error.response?.data?.detail ||
          error.response?.data?.name?.[0] ||
          JSON.stringify(error.response?.data) ||
          'Erro ao atualizar grupo.'
      )
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => groupsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      setDeleteTarget(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Erro ao excluir grupo.')
      setDeleteTarget(null)
    },
  })

  // Form handlers
  const openCreate = () => {
    setEditingGroup(null)
    setFormData(emptyForm)
    setFormError('')
    setShowForm(true)
  }

  const openEdit = (group: Group) => {
    setEditingGroup(group)
    setFormData({
      name: group.name,
      permissions: new Set(group.permissions),
    })
    setFormError('')
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingGroup(null)
    setFormData(emptyForm)
    setFormError('')
  }

  const togglePermission = (id: number) => {
    const newPerms = new Set(formData.permissions)
    if (newPerms.has(id)) {
      newPerms.delete(id)
    } else {
      newPerms.add(id)
    }
    setFormData({ ...formData, permissions: newPerms })
  }

  const toggleAppPermissions = (perms: Permission[]) => {
    const ids = perms.map((p) => p.id)
    const allSelected = ids.every((id) => formData.permissions.has(id))
    const newPerms = new Set(formData.permissions)
    if (allSelected) {
      ids.forEach((id) => newPerms.delete(id))
    } else {
      ids.forEach((id) => newPerms.add(id))
    }
    setFormData({ ...formData, permissions: newPerms })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!formData.name.trim()) {
      setFormError('Nome do grupo é obrigatório.')
      return
    }

    const payload: CreateGroupRequest = {
      name: formData.name.trim(),
      permissions: Array.from(formData.permissions),
    }

    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Grupos
        </h1>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors"
        >
          + Novo Grupo
        </button>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Buscar
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Nome do grupo..."
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total de Grupos</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {groups.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Permissões Disponíveis</p>
          <p className="text-2xl font-bold text-primary">
            {allPermissions.length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Carregando grupos...</p>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">🔐</div>
            <p className="text-gray-600 dark:text-gray-400">
              Nenhum grupo encontrado.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Nome
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Permissões
                  </th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredGroups.map((group) => (
                  <tr
                    key={group.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {group.name}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className="inline-flex px-2 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                        {group.permissions_count} permissões
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => openEdit(group)}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => setDeleteTarget(group)}
                          className="px-3 py-1 text-sm border border-danger rounded-btn text-danger hover:bg-danger hover:text-white transition-colors"
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeForm} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
              </h3>

              {formError && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome do Grupo *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Ex: Gestores, Avaliadores..."
                    required
                  />
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Permissões ({formData.permissions.size} selecionadas)
                  </label>
                  <div className="border border-gray-300 dark:border-gray-600 rounded-btn max-h-80 overflow-y-auto">
                    {permissionsByApp.map(([app, perms]) => {
                      const allSelected = perms.every((p) => formData.permissions.has(p.id))
                      const someSelected = perms.some((p) => formData.permissions.has(p.id))
                      return (
                        <div key={app} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                          <div
                            className="flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-gray-900/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50"
                            onClick={() => toggleAppPermissions(perms)}
                          >
                            <input
                              type="checkbox"
                              checked={allSelected}
                              ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected }}
                              onChange={() => toggleAppPermissions(perms)}
                              className="rounded border-gray-300 text-primary focus:ring-primary w-4 h-4"
                            />
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">
                              {app}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-500">
                              ({perms.length})
                            </span>
                          </div>
                          <div className="px-4 py-2 space-y-1">
                            {perms.map((perm) => (
                              <label
                                key={perm.id}
                                className="flex items-center gap-3 cursor-pointer py-1 hover:bg-gray-50 dark:hover:bg-gray-700/30 px-2 rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.permissions.has(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  className="rounded border-gray-300 text-primary focus:ring-primary w-4 h-4"
                                />
                                <div>
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {perm.name}
                                  </span>
                                  <span className="text-xs text-gray-500 dark:text-gray-500 ml-2">
                                    ({perm.codename})
                                  </span>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeForm}
                    disabled={isSaving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving
                      ? 'Salvando...'
                      : editingGroup
                      ? 'Salvar Alterações'
                      : 'Criar Grupo'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Excluir Grupo"
        description={
          <p>
            Tem certeza que deseja excluir o grupo <strong>{deleteTarget?.name}</strong>?
            Esta ação não pode ser desfeita.
          </p>
        }
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        isDanger
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default GroupsPage
